import { ApiOperationItems } from "~enums/apiOperationItems.ts";
import { useApiOperations } from "~hooks/useApiOperations.ts";
import type { InviteNotifyUserData } from "~pages/Users/hooks/useInviteNotifyUser.ts";
import type { UpsertUserPermissionData } from "~pages/Users/PermissionUserEditPage/types";
import type { AddUserForm } from "~pages/Users/PermissionUserListPage/types";
import type { User, UserResponse } from "~types/user";
import type { UserPermissionResponse } from "~types/userPermission";
import { isDefaultOperationSucceeded } from "~utils/defaultOperationResponse.ts";
import useAddPermissionUser from "./useAddPermissionUser.ts";
import useDeletePermissionUser from "./useDeletePermissionUser.ts";
import useDeleteUserPermission from "./useDeleteUserPermission.ts";
import useInviteNotifyUser from "./useInviteNotifyUser.ts";
import useUpsertUserPermission from "./useUpsertUserPermission.ts";

const isUserCreated = (response: UserResponse) => Boolean(response?.data?.length);
const isUserPermissionUpserted = (response: UserPermissionResponse) => Boolean(response?.data?.length);

type PermissionUserServiceOptions = {
  onUserAdded?: (user: User) => void;
};

export const usePermissionUserService = (options?: PermissionUserServiceOptions) => {
  const { mutateAsync: addPermissionUserFn, isPending: isAddPending } = useAddPermissionUser();
  const { mutateAsync: deletePermissionUserFn, isPending: isDeleteUserPending } = useDeletePermissionUser();
  const { mutateAsync: deleteUserPermissionFn, isPending: isDeletePermissionPending } = useDeleteUserPermission();
  const { mutateAsync: upsertUserPermissionFn, isPending: isUpsertPending } = useUpsertUserPermission();
  const { mutateAsync: inviteNotifyUserFn, isPending: isInviteNotifyPending } = useInviteNotifyUser();

  const { create, remove, execute } = useApiOperations(ApiOperationItems.PERMISSION_USER);
  const {
    create: createUserPermission,
    update: updateUserPermission,
    remove: removeUserPermission,
  } = useApiOperations(ApiOperationItems.USER_PERMISSION);

  const addPermissionUser = async (userData: AddUserForm) => {
    const response = await create(() => addPermissionUserFn(userData), {
      successMessageId: "permissions.user.actions.add.success",
      isSucceeded: isUserCreated,
    });

    if (response?.data?.[0]) {
      options?.onUserAdded?.(response.data[0]);
    }

    return response;
  };

  const deletePermissionUser = async (userId: number) =>
    Boolean(
      await remove(() => deletePermissionUserFn(userId), {
        successMessageId: "permissions.user.actions.delete.success",
        isSucceeded: isDefaultOperationSucceeded,
      }),
    );

  const deleteUserPermission = async (permissionId: number) =>
    Boolean(
      await removeUserPermission(() => deleteUserPermissionFn(permissionId), {
        successMessageId: "permissions.user.role.actions.delete.success",
        isSucceeded: isDefaultOperationSucceeded,
      }),
    );

  const upsertUserPermission = async (upsertModel: UpsertUserPermissionData) => {
    const successMessageId = `permissions.user.role.actions.${upsertModel.permissionId ? "edit" : "add"}.success`;
    const request = () => upsertUserPermissionFn(upsertModel);

    if (upsertModel.permissionId) {
      return updateUserPermission(request, {
        successMessageId,
        isSucceeded: isUserPermissionUpserted,
      });
    }

    return createUserPermission(request, {
      successMessageId,
      isSucceeded: isUserPermissionUpserted,
    });
  };

  const inviteNotifyUser = async (data: InviteNotifyUserData) => {
    const successMessageId = `permissions.user.actions.${data.isPendingUser ? "invite" : "notify"}.success`;

    return Boolean(
      await execute(() => inviteNotifyUserFn(data), {
        successMessageId,
        isSucceeded: isDefaultOperationSucceeded,
      }),
    );
  };

  return {
    addPermissionUser,
    isAddPending,
    deletePermissionUser,
    isDeleteUserPending,
    deleteUserPermission,
    isDeletePermissionPending,
    upsertUserPermission,
    isUpsertPending,
    inviteNotifyUser,
    isInviteNotifyPending,
  };
};
